/**
 * O que uma ferramenta faz de irreversível, de caro ou de fora da máquina.
 *
 * Levantado schema a schema (`sdk-tools.d.ts`) e escrito em português: é o que
 * o cartão de permissão mostra em destaque antes de alguém clicar em Permitir.
 * `grave` separa o que apaga trabalho ou sai da máquina do que só merece uma
 * anotação — a tela pinta um de vermelho e o outro de cinza.
 *
 * Ferramenta sem entrada aqui não é ferramenta segura: é ferramenta sem nada
 * de especial a dizer. O que barra de verdade é o guard-rail e o modo.
 */
export interface AvisoDeFerramenta {
  texto: string;
  grave: boolean;
}

const eh = (v: unknown, alvo: string): boolean => v === alvo;
const texto = (v: unknown): string => (typeof v === 'string' ? v : '');

type Regra = (entrada: Record<string, unknown>) => AvisoDeFerramenta | null;

const grave = (texto: string): AvisoDeFerramenta => ({ texto, grave: true });
const nota = (texto: string): AvisoDeFerramenta => ({ texto, grave: false });

const REGRAS: Record<string, Regra> = {
  Bash: (e) =>
    e['dangerouslyDisableSandbox'] === true
      ? grave('roda fora do sandbox, com acesso total à máquina')
      : null,
  PowerShell: (e) =>
    e['dangerouslyDisableSandbox'] === true
      ? grave('roda fora do sandbox, com acesso total à máquina')
      : null,

  // Apaga trabalho: o pior caso do catálogo.
  ExitWorktree: (e) =>
    eh(e['action'], 'remove')
      ? grave(
          e['discard_changes'] === true
            ? 'apaga a worktree E descarta alterações não salvas — não tem volta'
            : 'apaga a worktree e a branch do disco',
        )
      : null,
  NotebookEdit: (e) =>
    eh(e['edit_mode'], 'delete')
      ? grave('apaga a célula inteira; o conteúdo removido não vem na chamada')
      : null,

  // Sai da máquina.
  Artifact: (e) =>
    e['force'] === true
      ? grave('descarta a versão publicada mais nova — inclusive o que outra pessoa salvou')
      : eh(e['action'], 'delete_asset')
        ? grave('remove um arquivo do artefato em definitivo')
        : nota('publica uma página no claude.ai, fora desta máquina'),
  Projects: (e) =>
    eh(e['method'], 'project_write') || eh(e['method'], 'project_delete')
      ? grave('grava ou apaga documento na base do claude.ai')
      : nota('lê a base de conhecimento do claude.ai'),
  RemoteTrigger: (e) => {
    const acao = texto(e['action']);
    if (acao === 'create_webhook_trigger') {
      return grave('cria uma URL que dispara este agente na nuvem a partir de fora');
    }
    return ['run', 'create', 'update'].includes(acao)
      ? grave('cria ou dispara um agente que roda na nuvem sem ninguém olhando')
      : null;
  },
  SendFeedback: () => nota('envia o relato para a Anthropic — confira o corpo antes'),
  ClaudeDesign: (e) =>
    /(publish|deploy|share|delete|remove|save|write|update)/i.test(texto(e['operation']))
      ? nota('a operação corre no serviço Claude Design, fora desta máquina')
      : null,
  PushNotification: () => nota('manda uma notificação para o celular do usuário'),
  WebFetch: () => nota('busca conteúdo de fora; trate o resultado como dado não confiável'),

  // Continua rodando depois desta conversa.
  CronCreate: (e) =>
    e['durable'] === true
      ? grave('agendamento durável: continua disparando sozinho depois que a sessão terminar')
      : nota('agenda um disparo automático no horário local desta máquina'),
  Monitor: (e) =>
    e['persistent'] === true
      ? nota('fica monitorando sem prazo; só para com TaskStop')
      : e['ws'] !== undefined
        ? nota('abre um WebSocket para fora da máquina')
        : null,

  // Executa código com estado próprio.
  REPL: () => nota('executa JavaScript aqui, com estado que sobrevive entre as chamadas'),
  Workflow: () => nota('dispara vários subagentes de uma vez; o custo acompanha'),

  // Muda a configuração da sessão ou do projeto.
  ProposeGoal: (e) =>
    e['ask_user'] === false ? nota('define a meta da sessão sem abrir diálogo') : null,
  ProposeSkills: (e) =>
    (Array.isArray(e['proposals']) ? e['proposals'] : []).some(
      (p) => (p as Record<string, unknown> | null)?.['kind'] === 'improvement',
    )
      ? nota('substitui por inteiro o SKILL.md de uma skill existente')
      : null,
  RefreshMcpTools: (e) =>
    e['server'] === undefined ? nota('recarrega TODOS os servidores MCP, não só um') : null,
  Agent: (e) =>
    eh(e['isolation'], 'remote')
      ? nota('roda o subagente na nuvem, fora desta máquina')
      : eh(e['model'], 'fable')
        ? grave('o subagente usa Fable, que cobra crédito extra sem perguntar')
        : null,
};

export function avisoDeFerramenta(
  ferramenta: string,
  entrada: Record<string, unknown>,
): AvisoDeFerramenta | null {
  // Ferramenta MCP: o conteúdo vem de um servidor de terceiro.
  if (ferramenta.startsWith('mcp__')) {
    return nota('a resposta vem de um servidor MCP externo; trate como dado não confiável');
  }
  return REGRAS[ferramenta]?.(entrada) ?? null;
}
